package com.ilussobsa.views

import com.ilussobsa.*
import com.ilussobsa.Strings
import com.ilussobsa.liveauction.LiveAuctionScreen
import com.ilussobsa.sdk.*
import com.ilussobsa.utils.*
import com.ilussobsa.utils.checkCircle
import com.ilussobsa.utils.emptyCircle
import com.ilussobsa.views.LogInScreen.LoginScreenStage
import com.ilussobsa.views.LogInScreen.Notice
import com.ilussobsa.views.dialogs.GenericConfirmationDialog
import com.ilussobsa.views.dialogs.SendEmailDialog
import com.lightningkite.UUID
import com.lightningkite.atZone
import com.lightningkite.kiteui.QueryParameter
import com.lightningkite.kiteui.Routable
import com.lightningkite.kiteui.locale.RenderSize
import com.lightningkite.kiteui.locale.renderDateToString
import com.lightningkite.kiteui.locale.renderToString
import com.lightningkite.kiteui.models.*
import com.lightningkite.kiteui.navigation.*
import com.lightningkite.kiteui.reactive.*
import com.lightningkite.kiteui.reactive.*
import com.lightningkite.kiteui.report
import com.lightningkite.kiteui.views.*
import com.lightningkite.kiteui.views.direct.*
import com.lightningkite.kiteui.views.l2.Recycler2
import com.lightningkite.kiteui.views.l2.RecyclerViewPlacerHorizontalGrid
import com.lightningkite.kiteui.views.l2.children
import com.lightningkite.kiteui.views.l2.field
import com.lightningkite.kiteui.views.l2.icon
import com.lightningkite.lightningdb.*
import com.lightningkite.lightningserver.LsErrorException
import com.lightningkite.lightningserver.auth.proof.FinishProof
import com.lightningkite.lightningserver.auth.proof.IdentificationAndPassword
import com.lightningkite.now
import com.lightningkite.nowLocal
import com.lightningkite.reactive.context.*
import com.lightningkite.reactive.core.*
import com.lightningkite.reactive.extensions.*
import com.lightningkite.reactive.lensing.*
import com.lightningkite.readable.*
import com.lightningkite.serialization.lensPath
import com.lightningkite.serialization.notNull
import kotlin.math.absoluteValue
import kotlin.math.max
import kotlin.math.min
import kotlin.time.Duration.Companion.minutes
import kotlin.time.Duration.Companion.seconds
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.datetime.*

@Routable("/")
class DashboardScreen : Page {

    @QueryParameter("fromStripe")
    val fromStripe = Signal(false)

    private val showVerification = remember {
        stepForVerificationForm(currentUser(), fromStripe()) != null
    }

    override fun ViewWriter.render() = scrolling - col {
        centered - sizeConstraints(width = 1200.dp) - col {
            launch {
                if (fromStripe()) {
                    val session = currentUserSession()
                    val updatedUserRole = session.uncached.checkStripe()
                    if (session.me()?.role != updatedUserRole) session.user[session.userId].invalidate()
                }
            }

            verificationCard()

            launch {
                currentUser()?.let {
                    if (showVerification() && it.firstLogin == null) {
                        if (dialogPageNavigator.currentPage() !is VerificationDialog) {
                            dialogPageNavigator.navigate(VerificationDialog(fromStripe))
                        }
                    }
                }
            }

            liveAuctionsCard()

            upcomingAuctionsCard()

            pastAuctionsCard()

            sizedBox(SizeConstraints(width = 95.rem)) - centered - col {
                ::shown { currentUser()?.role?.let { it > UserRole.Customer } == true }
                statisticsBlock()
            }
        }
    }

    private fun ViewWriter.pastAuctionsCard() {
        card - col {
            row {
                centered - icon(Icon.pastAuctions, Strings.pastAuctions)
                centered - h2(Strings.pastAuctions)
                expanding - space()
                compact - CriticalSemantic.onNext - link {
                    centered - underline - text(Strings.seeMore)
                    to = { PastAuctionsScreen() }
                }
            }

            val pastQuery = Query<Vehicle>(
                orderBy = sort { it.completion.notNull.at.descending() },
                limit = 3,
            ) {
                it.completion neq null
            }
            val pastToListen = remember {
                currentSession().vehicle.query(pastQuery)
            }
            val pastItems = remember { pastToListen()().sortedByDescending { it.completion?.at }.take(3) }
            forEachUpdating(
                remember { pastItems().toList() }
            ) {
                dashboardPreviousAuctions(it)
            }
        }
    }

    private fun ViewWriter.upcomingAuctionsCard() {
        card - col {
            val upcomingAuctions = remember {
                val session = currentSession()
                val localDateToday = now().toLocalDateTime(TimeZone.currentSystemDefault()).date
                val localEndDate = localDateToday + DatePeriod(days = 30)
                val startTimeLocal = localDateToday.atStartOfDayIn(TimeZone.currentSystemDefault())
                val endTimeLocal = localEndDate.atStartOfDayIn(TimeZone.currentSystemDefault())
                val startTimeServer = startTimeLocal.toLocalDateTime(auctionZone)
                val endTimeServer = endTimeLocal.toLocalDateTime(auctionZone)

                session.auctionLane.query(Query(orderBy = sort { it.scheduledStart }) {
                    (it.scheduledStart lt endTimeServer) and (it.scheduledStart gte startTimeServer)
                })().sortedBy { it.scheduledStart }
            }

            val items = remember {
                upcomingAuctions().mapNotNull { auctionLane ->
                    currentSession().vehicle.query(Query(orderBy = sort { it.orderingValue }, limit = 1) {
                        it.auctionLane.eq(auctionLane._id) and it.submitted.neq(null) and
                                it.archived.eq(null) and it.completion.eq(null)
                    })().firstOrNull()
                }
            }

            row {
                centered - icon(Icon.upcomingAuctions, Strings.upcomingAuctions)
                centered - h2(Strings.upcomingAuctions)
                expanding - space()
                compact - CriticalSemantic.onNext - link {
                    centered - underline - text(Strings.seeMore)
                    ::to{ { UpcomingAuctionsScreen() } }
                }
            }
            frame {
                lateinit var scroll: ScrollingBehaviors
                swapView {
                    swapping(
                        current = { AppState.windowInfo().width <= 1000.dp },
                        views = { useMiniCards ->
                            scrolling(horizontal = true, vertical = false) {
                                scroll = this
                            } - row {
                                forEachUpdating(
                                    items = remember { items().withIndex().toList() },
                                ) {
                                    frame {
                                        val vehicle = remember { it().value }

                                        if (useMiniCards) {
                                            sizeConstraints(width = 340.dp, height = 400.dp)
                                        } else {
                                            sizeConstraints(width = 480.dp, height = 400.dp)
                                        }
                                        upcomingAuctionCard(vehicle)
                                    }
                                }
                            }
                        }
                    )
                }
                // Previous Button
                align(Align.Start, Align.Center) - sizeConstraints(
                    width = 3.rem,
                    height = 3.rem
                ) - card - button {
                    spacing = 0.dp
                    ::shown {
                        val vp = scroll.viewport()
                        val content = scroll.content()
                        (vp.left - content.left) > 20
                    }
                    centered - icon(
                        Icon.chevronLeft,
                        "Previous vehicle"
                    )
                    onClick {
                        val vp = scroll.viewport()
                        val newVpLeft = (vp.left - 200).coerceAtLeast(scroll.content().left)
                        scroll.scrollTo(newVpLeft, vp.top, true)
                    }
                }
                // Next Button
                align(Align.End, Align.Center) - sizeConstraints(
                    width = 3.rem,
                    height = 3.rem
                ) - card - button {
                    spacing = 0.dp
                    ::shown {
                        val vp = scroll.viewport()
                        val content = scroll.content()
                        ((content.right - vp.width) - vp.left) > 20
                    }
                    centered - icon(
                        Icon.chevronRight,
                        "Next vehicle"
                    )
                    onClick {
                        val vp = scroll.viewport()
                        val content = scroll.content()
                        val newVpLeft = (vp.left + 200).coerceAtMost(content.right - vp.width)
                        scroll.scrollTo(newVpLeft, vp.top, true)
                    }
                }
            }
        }
    }

    private fun ViewWriter.liveAuctionsCard() {
        card - col {
            val todayAuction = remember {
                val session = currentSession()

                val localDateToday = now().toLocalDateTime(TimeZone.currentSystemDefault()).date
                val localDateTomorrow = localDateToday + DatePeriod(days = 1)
                val startTimeLocal = localDateToday.atStartOfDayIn(TimeZone.currentSystemDefault())
                val endTimeLocal = localDateTomorrow.atStartOfDayIn(TimeZone.currentSystemDefault())
                val startTimeServer = startTimeLocal.toLocalDateTime(auctionZone)
                val endTimeServer = endTimeLocal.toLocalDateTime(auctionZone)
                session.auctionLane.query(
                    Query(
                        condition {
                            (it.scheduledStart lt endTimeServer) and (it.scheduledStart gte startTimeServer)
                        },
                        orderBy = sort { it.scheduledStart },
                        limit = 1
                    )
                )().firstOrNull()
            }

            val query = remember {
                Query(
                    condition<Vehicle> {
                        Condition.And(
                            listOfNotNull(
                                condition { it.completion eq null },
                                condition { it.submitted neq null },
                                condition { it.archived eq null },
                                condition { it.auctionLane.notNull eqNn todayAuction()?._id }
                            )
                        )
                    },
                    limit = 5,
                )
            }
            val vehicle = remember {
                currentSession.awaitNotNull().vehicle.query(query())().firstOrNull()
            }

            shown = false
            ::shown { vehicle() != null }

            val id = remember { vehicle()?._id }
            val notify = remember {
                id()?.let { vehicleRelationshipUpsertProp(it, VehicleRelationship.path.notifications) }
                    ?: Signal(false)
            }.flatten()

            val auctionDate = remember { todayAuction()?.scheduledStartInstant }

            row {
                centered - icon(Icon.liveAuction, "Live Auctions")
                centered - h2(Strings.liveAuctions)
            }
            row {
                expanding - h1 {
                    ::content {
                        vehicle()?.mmt ?: ""
                    }
                }
                align(Align.End, Align.Start) - row {
                    ::shown {
                        !isSeller() && vehicle()?.liveAt == null
                    }
                    compact - iconButton - toggleButton {
                        icon {
                            ::source { Icon.notifications2 }
                            description = Strings.notificationsEnabled
                        }
                        checked bind notify
                    }
                }
            }
            rowCollapsingToColumn(800.dp) {
                DangerSemantic.plus(CardSemantic).onNext - row {
                    centered - icon {
                        ::source { Icon.calendar }
                        description = "Auction date"
                    }
                    bold - centered - text {
                        ::shown{ auctionDate() != null }
                        ::content label@{
                            val asZoned = auctionDate()?.atZone(auctionZone) ?: return@label ""
                            buildString {
                                append(asZoned.time.renderToString())
                                append(" ")
                                append("PST")
                                append(" - ")
                                append(asZoned.date.renderToString(RenderSize.Numerical))
                            }
                        }
                    }
                }
                expanding - space()

                CriticalSemantic.plus(ButtonSemantic).onNext - button {
                    paddingByEdge = Edges(1.rem, 0.5.rem, 1.rem, 0.5.rem)
                    centered - text("Preview")
                    onClick { pageNavigator.navigate(VehicleDetailScreen(vehicle()?._id ?: nullUuid)) }
                }

                val nowBySecond = reactiveProcess {
                    while (true) {
                        emit(now())
                        delay(1.seconds)
                    }
                }

                val countdown = remember {
                    todayAuction()?.let { it.scheduledStartInstant - nowBySecond() }
                }

                WarningSemantic.plus(ButtonSemantic).onNext - button {
                    ::shown {
                        countdown()?.let { it <= 15.minutes } == true
                    }
                    paddingByEdge = Edges(1.rem, 0.5.rem, 1.rem, 0.5.rem)
                    centered - text {
                        ::content {
                            countdown()?.let {
                                it.toComponents { minutes, seconds, nanoseconds ->
                                    if (it > 0.seconds) {
                                        val min = minutes.absoluteValue.toString().padStart(2, '0')
                                        val sec = seconds.absoluteValue.toString().padStart(2, '0')
                                        "Join: $min:$sec"
                                    } else {
                                        "Join Now! Auction in Progress"
                                    }
                                }
                            } ?: ""
                        }
                    }
                    dynamicTheme {
                        LiveSemantic.takeIf { countdown()?.let { it <= 0.seconds } == true }
                    }
                    onClick { pageNavigator.navigate(LiveAuctionScreen(todayAuction()?._id ?: nullUuid)) }
                }
            }
            compact - rowCollapsingToColumn(500.dp) {
                compact - row {
                    centered - WarningSemantic.onNext - text(Strings.year2)
                    centered - text {
                        ::content { "${vehicle()?.year ?: "----"}" }
                    }
                }
                space()
                compact - row {
                    centered - WarningSemantic.onNext - text(Strings.mileage2)
                    centered - text {
                        ::content{
                            vehicle()?.odometer?.toCommaString() ?: Strings.noMileageInformation
                        }
                    }
                }
                expanding - space()
                compact - row {
                    val proxyBid = remember {
                        vehicle()?._id?.let {
                            vehicleRelationshipUpsertProp(
                                it,
                                VehicleRelationship.path.autobid
                            )()
                        }
                    }
                    ::shown {
                        participatesInAuctions() && proxyBid() != null
                    }

                    centered - WarningSemantic.onNext - text(Strings.yourProxyBid)
                    centered - text { ::content{ proxyBid()?.renderKPriceInDollars() ?: "" } }
                }
            }
            image {
    //                    scaleType = ImageScaleType.Fit
                ::source { vehicle()?.coverImage?.let { ImageRemote(it.location) } }
            }
        }
    }

    private fun ViewWriter.verificationCard() = col {
        shown = false
        ::shown {
            currentUser()?.let { it.firstLogin != null } != false && showVerification()
        }
        card - verificationForm(fromStripe)
    }

}

private fun ViewWriter.dashboardPreviousAuctions(vehicle: Reactive<Vehicle>): ViewModifiable {
    return col {
        compact - row {
            gravity(Align.Start, Align.End) - h4 { ::content{ vehicle().mmt } }
            expanding - space()
            gravity(Align.End, Align.End) - text {
                ::content{
                    Strings.date + vehicle().completion?.at?.renderDateToString(
                        RenderSize.Numerical
                    )
                }
            }
        }
        separator()
        compact - row {
            weight(1f) - col {
                sizedBox(SizeConstraints(height = 6.rem, width = 9.rem)) - image {
                    scaleType = ImageScaleType.Crop
                    ::source { vehicle().thumbnail?.let { ImageRemote(it.location) } }
                }
            }
            important - weight(3f) - frame {
                centered - col {
                    centered - CriticalSemantic.onNext - h3 { ::content{ vehicle().completion?.bids.toString() } }
                    spacingOverrideBeforeNext(0.dp)
                    centered - CriticalSemantic.onNext - text(Strings.bids2)
                }
            }
            important - weight(3f) - frame {
                centered - col {
                    centered - CriticalSemantic.onNext - h3 {
                        ::content{
                            vehicle().completion?.startingBid?.renderKPriceInDollars() ?: "-"
                        }
                    }
                    spacingOverrideBeforeNext(0.dp)
                    centered - CriticalSemantic.onNext - text(Strings.startingBid)
                }
            }
            important - weight(3f) - frame {
                centered - col {
                    centered - CriticalSemantic.onNext - h3 {
                        ::content{
                            vehicle().completion?.price?.renderKPriceInDollars() ?: "-"
                        }
                    }
                    spacingOverrideBeforeNext(0.dp)
                    centered - CriticalSemantic.onNext - text(Strings.finalBid)
                }
            }
        }
    }
}

fun ViewWriter.statisticsBlock() {
    card - col {
        bold - h2(Strings.statistics)
        fun ViewWriter.stat(label: String, value: suspend () -> String) {
            centered - sizeConstraints(width = 30.rem) - row {
                text(label)
                expanding - col {
                    expanding - frame {}
                    separator()
                    expanding - frame {}
                }
                text {
                    align = Align.End
                    reactiveSuspending { content = value() }
                }
            }
        }

        fun ViewWriter.statSet(filter: Condition<Vehicle>) {
//                onlyWhen { isSeller() } -
            stat(Strings.vehiclesSold) {
                currentSession().vehicle.skipCache.count(condition {
                    it.completion.notNull.sold.eq(true) and filter
                }).toString()
            }
//                onlyWhen { isSeller() } -
            stat(Strings.saleValueTotal) {
                (currentSession().vehicle.skipCache.aggregate(AggregateQuery(Aggregate.Sum, condition {
                    it.completion.notNull.sold.eq(true) and filter
                }, Vehicle.path.completion.notNull.price))?.toInt() ?: 0).renderPriceInDollars()
            }
//                onlyWhen { !isSeller() } -
            stat(Strings.vehiclesPurchased) {
                currentSession().vehicle.skipCache.count(condition {
                    it.completion.notNull.winner.eq(currentUser()?._id) and
                            it.completion.notNull.sold.eq(true) and
                            filter
                }).toString()
            }
//                onlyWhen { !isSeller() } -
            stat(Strings.purchaseValueTotal) {
                (currentSession().vehicle.skipCache.aggregate(AggregateQuery(Aggregate.Sum, condition {
                    it.completion.notNull.winner.eq(currentUser()?._id) and
                            it.completion.notNull.sold.eq(true) and
                            filter
                }, Vehicle.path.completion.notNull.price))?.toInt() ?: 0).renderPriceInDollars()
            }
        }
        col {
            val monday =
                nowLocal().date.previousOrCurrentWeekday(DayOfWeek.MONDAY).atStartOfDayIn(TimeZone.UTC)
            val filter = condition<Vehicle> {
                it.completion.notNull.at.gt(monday)
            }

            h4(Strings.lastWeek)
            statSet(filter)
        }
        col {
            val filter = condition<Vehicle>(true)
            h4(Strings.allTime)
            statSet(filter)
        }
    }
}


fun ViewWriter.vehicleLink(vehicle: Reactive<Vehicle>) {
    card - button {
        onClick {
            if (useTwoPane.await()) {
                dialogPageNavigator.navigate(
                    SellingDialogWrapper(VehicleDetailScreen(vehicle.await()._id))
                )
            } else {
                this.pageNavigator.navigate(VehicleDetailScreen(vehicle.await()._id))
            }

        }
        col {
            sizeConstraints(width = 20.rem, height = 12.rem) - image {
                description = ""
                ::source { vehicle().thumbnail?.let { ImageRemote(it.location) } }
                scaleType = ImageScaleType.Crop
            }
            compact - col {
                h6 { ::content { vehicle().run { "${year ?: ""}  ${make ?: ""} ${model ?: ""}" } } }
                subtext { ::content { vehicle().statusDescription() } }
                row {
                    centered - subtext {
                        ::content {
                            vehicle().odometer?.let { "${it.toCommaString()} mi" }
                                ?: Strings.noMileageInformation
                        }
                    }
                    centered - subtext("|")
                    centered - issueIcons(vehicle, size = 1.rem)
                }
            }
        }
    }
}
